import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Eraser, ImagePlus, Lock, RefreshCw, Trash2, UploadCloud } from 'lucide-react';
import {
  addModelPhoto,
  deleteModelPhoto,
  listModelPhotos,
  pruneLegacySyncModelPhotos,
  selectModelPhoto
} from '../api/models';
import { uploadFile } from '../api/files';
import { AdminModelPhotoView, AdminModelView } from '../api/types';
import { pickImageUrl } from '../utils/imageUrls';

interface ModelPhotoGalleryProps {
  model: AdminModelView;
  onChanged?: () => void;
  onNotify?: (type: string, message: string) => void;
}

const cleanKey = (key: string) => {
  const trimmed = key.trim();
  const slash = trimmed.lastIndexOf('/');
  return slash >= 0 && slash < trimmed.length - 1 ? trimmed.slice(slash + 1) : trimmed;
};

const formatSource = (source?: string | null) => {
  if (!source) return 'photo';
  if (source === 'LEGACY_IMPORT') return 'imported';
  return source.toLowerCase();
};

const bestUrl = (photo: AdminModelPhotoView) => pickImageUrl(photo.imageUrls, photo.fileName);

const ModelPhotoGallery: React.FC<ModelPhotoGalleryProps> = ({ model, onChanged, onNotify }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const photosQuery = useQuery({
    queryKey: ['model-photos', model.id],
    queryFn: () => listModelPhotos(model.id)
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['model-photos', model.id] }),
      queryClient.invalidateQueries({ queryKey: ['model', model.id] }),
      queryClient.invalidateQueries({ queryKey: ['models'] })
    ]);
    onChanged?.();
  };

  const selectMutation = useMutation({
    mutationFn: (photoId: number) => selectModelPhoto(model.id, photoId),
    onSuccess: async () => {
      onNotify?.('success', 'Profile photo selected and pinned');
      await invalidate();
    },
    onError: () => onNotify?.('error', 'Failed to select photo')
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const response = await uploadFile(file, `${model.modelUsername}-avatar-${timestamp}${ext}`);
      const fileName = cleanKey(Object.values(response)[0] as string);
      const photo = await addModelPhoto(model.id, { fileName, source: 'MANUAL' });
      return selectModelPhoto(model.id, photo.id);
    },
    onSuccess: async () => {
      setUploadPreview(null);
      setUploadError(null);
      onNotify?.('success', 'Photo uploaded and pinned');
      await invalidate();
    },
    onError: (error) => {
      setUploadPreview(null);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload photo');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (photo: AdminModelPhotoView) => deleteModelPhoto(model.id, photo.id),
    onSuccess: async () => {
      onNotify?.('success', 'Photo deleted from gallery and storage');
      await invalidate();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Photo cannot be deleted';
      onNotify?.('error', message);
    }
  });

  const pruneLegacyMutation = useMutation({
    mutationFn: () => pruneLegacySyncModelPhotos(model.id, { execute: true, deleteObjects: true }),
    onSuccess: async (result) => {
      const failed = result.objectDeleteFailures?.length || 0;
      const suffix = failed > 0 ? `, ${failed} MinIO delete failed` : '';
      onNotify?.(
        failed > 0 ? 'error' : 'success',
        `Legacy sync cleanup removed ${result.rowsRemoved} rows and ${result.objectsDeleted} objects${suffix}`
      );
      await invalidate();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Legacy sync cleanup failed';
      onNotify?.('error', message);
    }
  });

  const photos = photosQuery.data || [];
  const currentPhoto = useMemo(
    () => photos.find(photo => photo.current) || photos.find(photo => photo.fileName === model.imageFilename),
    [photos, model.imageFilename]
  );
  const currentUrl = currentPhoto ? bestUrl(currentPhoto) : pickImageUrl(model.imageUrls, model.imageFilename);

  const processUpload = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      setUploadError('Maximum size is 30MB');
      return;
    }
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    uploadMutation.mutate(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processUpload(file);
  };

  const confirmDelete = (photo: AdminModelPhotoView) => {
    if (photo.current) {
      onNotify?.('error', 'Select another photo before deleting the current one');
      return;
    }
    if (!window.confirm(`Delete ${photo.fileName} from server storage?`)) return;
    deleteMutation.mutate(photo);
  };

  const confirmPruneLegacySync = () => {
    if (!window.confirm(`Remove old OT/PPC sync photos from ${model.modelUsername} gallery? Current photo will be kept.`)) return;
    pruneLegacyMutation.mutate();
  };

  return (
    <div className="model-photo-gallery">
      {uploadError && <div className="img-uploader-error">{uploadError}</div>}

      <div className="mpg-current">
        <div className="mpg-current-image">
          {uploadPreview || currentUrl ? (
            <img src={uploadPreview || currentUrl || ''} alt={model.modelName || model.modelUsername} />
          ) : (
            <ImagePlus size={28} />
          )}
          {uploadMutation.isPending && (
            <div className="mpg-busy">
              <RefreshCw size={16} />
              Uploading
            </div>
          )}
        </div>
        <div className="mpg-current-meta">
          <div className="mpg-title-row">
            <span className="mpg-title">Profile photo</span>
            {model.manualImage ? <span className="hf-tag warning sm"><Lock size={10} /> manual pin</span> : <span className="hf-tag sm">auto-sync</span>}
          </div>
          <span className="hf-mono mpg-file">{model.imageFilename || 'No image selected'}</span>
          <div className="mpg-actions">
            <button
              type="button"
              className="hf-btn sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending || pruneLegacyMutation.isPending}
            >
              <UploadCloud size={13} /> Upload photo
            </button>
            <button
              type="button"
              className="hf-btn ghost danger sm"
              onClick={confirmPruneLegacySync}
              disabled={uploadMutation.isPending || pruneLegacyMutation.isPending}
              title="Remove old onlytraffic/ppc sync photos from this gallery"
            >
              {pruneLegacyMutation.isPending ? <RefreshCw size={13} className="mpg-spin" /> : <Eraser size={13} />}
              Clean OT/PPC
            </button>
          </div>
        </div>
      </div>

      <div
        className={`mpg-dropzone${dragActive ? ' is-dragging' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
        onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
        onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadCloud size={14} />
        <span>Drop image here or click to upload</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) processUpload(file);
        }}
        style={{ display: 'none' }}
      />

      <div className="mpg-grid">
        {photosQuery.isLoading && <div className="mpg-empty">Loading gallery...</div>}
        {!photosQuery.isLoading && photos.length === 0 && <div className="mpg-empty">No saved photos yet</div>}
        {photos.map(photo => {
          const url = bestUrl(photo);
          const busy = selectMutation.isPending || deleteMutation.isPending || pruneLegacyMutation.isPending;
          return (
            <div key={photo.id} className={`mpg-card${photo.current ? ' is-current' : ''}`}>
              <div className="mpg-thumb">
                {url ? <img src={url} alt="" loading="lazy" /> : <ImagePlus size={20} />}
                {photo.current && <span className="mpg-current-badge"><Check size={11} /> current</span>}
                {photo.rowSource && <span className="mpg-source-badge">{photo.rowSource}</span>}
              </div>
              <div className="mpg-card-body">
                <div className="mpg-card-top">
                  <span className="hf-tag sm">{formatSource(photo.source)}</span>
                  <span className="hf-mono mpg-id">#{photo.id}</span>
                </div>
                <span className="hf-mono mpg-file" title={photo.fileName}>{photo.fileName}</span>
                <div className="mpg-card-actions">
                  <button
                    type="button"
                    className="hf-btn ghost sm"
                    onClick={() => selectMutation.mutate(photo.id)}
                    disabled={busy || photo.current}
                  >
                    <Check size={12} /> Select
                  </button>
                  <button
                    type="button"
                    className="hf-btn ghost danger sm"
                    onClick={() => confirmDelete(photo)}
                    disabled={busy || photo.current}
                    title={photo.current ? 'Select another photo before deleting' : 'Delete from server'}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModelPhotoGallery;
